import { lazy, Suspense, useMemo } from 'react'
import { Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { Head, useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { DiscoverPageLayout } from '@app/pages/layout/DiscoverPageLayout'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import useTransform from '@app/util/useTransform'
import { ViewEventModel } from 'functions/app/event/view'
import { RightColumn } from './sections/RightColumn'
import { Paragraph } from '@app/components/core/typography/Paragraph'
import { ClientOnly } from '@app/components/blocks/clientOnly/ClientOnly'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { DataCard } from './sections/DataCard'

const SingleMarkerMap = lazy(() =>
  import('@app/components/core/map/SingleMarkerMap').then((module) => ({
    default: module.SingleMarkerMap,
  })),
)

export type EventPageProps = {
  eventSlug: string
}

export const EventPage = ({ eventSlug }: EventPageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const eventQuery = useQuery('view-event', { slug: eventSlug })
  const event: ViewEventModel | null = eventQuery.data

  const eventImages = useMemo(() => {
    const images = event?.images ?? []
    return images.map((image) => image.src)
  }, [event?.images])

  if (!event) {
    return (
      <PageLayout gap="2rem">
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.agenda}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <PageLayout gap="0rem">
        <Column alignItems="stretch" gap="1.5rem">
          <Column>
            <Title.H3>{event.title}</Title.H3>
            <Text.Medium>{event.intro}</Text.Medium>
          </Column>
          <PageMediaGallery
            title={event.title}
            images={eventImages}
            defaultImage={defaultProjectPicture}
          />
          <DataCard event={event} />
        </Column>
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="3.5rem" standardPageTitle={false}>
      <Head>
        <title>{`${event.title} | CrowdBuilding`}</title>
        <meta name="description" content={event.intro} />
        {eventImages.length > 0 && (
          <meta property="og:image" content={eventImages[0]} />
        )}
      </Head>

      <Column gap="2rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.agenda}
          level3={event.title}
        />

        <DiscoverPageLayout sidebar={<RightColumn event={event} />}>
          <Title.H2>{event.title}</Title.H2>

          <SafeHtml html={event.content} />

          <div>
            <Title.H3>{translations.eventPage.address}</Title.H3>
            <Paragraph.Medium>{event.location.address}</Paragraph.Medium>
            {typeof event?.location?.geo?.lat === 'number' &&
              typeof event?.location?.geo?.long === 'number' && (
                <Suspense fallback={null}>
                  <ClientOnly fallback={null}>
                    <SingleMarkerMap
                      lat={event.location.geo.lat}
                      long={event.location.geo.long}
                      marker="pin"
                      zoom={8}
                      height="18rem"
                      style={{ borderRadius: '1.875rem' }}
                    />
                  </ClientOnly>
                </Suspense>
              )}
          </div>
        </DiscoverPageLayout>
      </Column>
    </PageLayout>
  )
}
